import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  kennzahlen as holeKennzahlen,
  stand,
  type Systemkennzahlen,
  type Umsystemstand,
  ZUSTANDSMARKE,
  ZUSTANDSNAME,
} from '../api/status';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Fehlerablage } from './Fehlerablage';
import { Hintergrundlaeufe } from './Hintergrundlaeufe';
import { genau, vorWieLange } from './zeit';

/**
 * Die Statusseite (BET-22, ERR-15).
 *
 * ============================================================================
 * WOFÜR SIE DA IST
 * ============================================================================
 *
 * Für den Augenblick, in dem jemand am Telefon fragt, warum die Bestätigung
 * nicht angekommen ist. Die Seite soll in drei Sekunden beantworten, ob es an
 * uns liegt — und wenn ja, seit wann.
 *
 * BET-22 verlangt sie ausdrücklich „auch für Empfang und Disposition". Sie
 * hängt deshalb an einem eigenen Recht (`umsystem:sehen`) und nicht am
 * Verwaltungsrecht; sonst sähe sie genau der nicht, der am Tresen steht.
 *
 * ============================================================================
 * DIE ZWEI ZEITEN SIND NICHT DASSELBE
 * ============================================================================
 *
 * `letzteVerbindung` ist die letzte ERFOLGREICHE Verbindung, `geprueftAm` der
 * letzte Versuch. Bei einer laufenden Störung steht die erste still und die
 * zweite läuft weiter — genau diese Differenz ist die Auskunft: „seit
 * 14:02 nicht erreichbar, zuletzt geprüft vor 12 Sekunden".
 *
 * Stünde nur eine Zeit da, wäre die Seite entweder unbrauchbar (man wüsste
 * nicht, wie alt die Angabe ist) oder irreführend (man hielte die letzte
 * Prüfung für die letzte Verbindung).
 *
 * ============================================================================
 * DIE KENNZAHLEN STEHEN UNTER DEM ZUSTAND
 * ============================================================================
 *
 * „Geht es der Anlage gut?" hat zwei Hälften: Erreichen wir die Umsysteme —
 * und wie steht es um uns selbst. Die zweite beantwortet der Block darunter:
 * welcher Stand läuft (BP-41), wie groß die Ablage ist, wie viel darin liegt.
 *
 * Sie stehen HINTER dem Zustand, weil die Frage bei einer Störung in dieser
 * Reihenfolge kommt: In der Kachelreihe (Seitentyp Übersicht, ÄA-41) steht
 * die Störungskachel vorn, die Kacheln der Anlage folgen; die Karten mit
 * Stand, Bestand und Tabellen stehen unter den Umsystemen. Und sie laden
 * ihren eigenen Takt: Der Zustand ändert sich im Minutentakt, die Größe der
 * Datenbank in Stunden.
 *
 * KEINE BETRÄGE. Die Seite ist auch für den Empfang (BET-22); eine
 * Zeilenzahl trägt keinen Namen und keinen Umsatz, eine Summe schon.
 */

/** Eine Zahl mit Tausenderpunkten — 16103 liest sich als 16.103. */
function zahl(wert: number): string {
  return wert.toLocaleString('de-DE');
}

export function Systemzustand(): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Umsystemstand[]>([]);
  const [zahlen, setzeZahlen] = useState<Systemkennzahlen | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laedt, setzeLaedt] = useState(true);
  /* ERR-07 (CRM-208): Die Fehlerablage läuft im Takt der Seite mit, ohne eigenen. */
  const [takt, setzeTakt] = useState(0);

  const laden = useCallback(async (hintergrund = false): Promise<void> => {
    try {
      setzeZeilen(await stand(hintergrund));
      setzeFehler(undefined);
    } catch (f) {
      if (f instanceof ApiFehler) setzeFehler(f);
      else throw f;
    } finally {
      setzeLaedt(false);
    }
  }, []);

  /*
   * Alle 30 s nachladen — der Statuslauf misst im Minutentakt, die Seite
   * holt zweimal so oft. Wer sie offen liegen lässt, während jemand eine
   * Störung behebt, soll die Erholung sehen, ohne neu zu laden.
   */
  useEffect(() => {
    void laden();
    // BEN-07: Der Takt hält die Sitzung nicht wach, nur der erste Abruf zählt.
    const uhr = setInterval(() => {
      void laden(true);
      setzeTakt((t) => t + 1);
    }, 30_000);
    return () => clearInterval(uhr);
  }, [laden]);

  /*
   * DIE KENNZAHLEN HABEN IHREN EIGENEN TAKT — fünf Minuten statt dreißig
   * Sekunden.
   *
   * Die Größe einer Datenbank ändert sich in Stunden. Sie im Takt des
   * Umsystemzustands mitzuladen hieße, zweimal in der Minute nach ihr zu
   * fragen — und `pg_database_size` liest dafür das ganze Verzeichnis.
   *
   * EIN EIGENER FEHLERWEG: Bleiben sie aus, ist die Seite trotzdem
   * brauchbar. Der Zustand der Umsysteme ist die Antwort, für die jemand
   * hierherkommt; die Kennzahlen sind das, was er danach noch wissen will.
   */
  useEffect(() => {
    const holen = (hintergrund: boolean): void => {
      void holeKennzahlen(hintergrund)
        .then(setzeZahlen)
        .catch(() => setzeZahlen(null));
    };
    holen(false);
    // BEN-07: wie oben.
    const uhr = setInterval(() => holen(true), 300_000);
    return () => clearInterval(uhr);
  }, []);

  const gestoert = zeilen.filter((z) => z.zustand === 'gestoert' || z.zustand === 'nicht_erreichbar');
  const wesentlichGestoert = gestoert.filter((z) => z.wesentlich);

  /*
   * STÖRUNGEN IN ROT ODER ROST (ÄA-41): Rot, wenn ein wesentliches System
   * fehlt — dann steht der Betrieb —, Rost, wenn nur Unwesentliches klemmt.
   * Ruhe trägt keine Farbe; sie ist der Regelfall.
   */
  const stoerungsklasse =
    wesentlichGestoert.length > 0 ? 'kachel--krit' : gestoert.length > 0 ? 'kachel--warn' : 'kachel--still';

  return (
    /* Seitentyp Übersicht (ÄA-41): Kacheln oben, darunter Karten. */
    <div className="uebersicht">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <p className="hilfe">
        Der Zustand der angebundenen Systeme, gemessen im Minutentakt. „Zuletzt
        erreicht“ nennt die letzte <strong>erfolgreiche</strong> Verbindung — bei einer
        laufenden Störung steht sie still, während „geprüft“ weiterläuft.
      </p>

      {/*
        Die Zusammenfassung steht ÜBER der Tabelle und nicht darunter: Wer die
        Seite aufruft, will zuerst wissen, ob überhaupt etwas klemmt. Die
        Einzelheiten liest er erst, wenn die Antwort ja lautet.

        Die Kacheln führen nirgendwohin und sind deshalb keine Knöpfe. Eine
        farbige trägt trotzdem nicht `kachel--still`: Dessen Regel für die
        Maus setzt die weiße Fläche und nähme ihr die Farbe.
      */}
      {!laedt && (
        <div className="kacheln">
          {/* BET-22, ERR-15 */}
          <div className={`kachel ${stoerungsklasse}`} role="status">
            <div className="kachel__wort">Störungen</div>
            <div className="kachel__zahl">{gestoert.length}</div>
            {gestoert.length === 0 ? (
              <div className="kachel__zusatz">Alles erreichbar</div>
            ) : (
              <div className="kachel__zusatz">
                {gestoert.map((z) => z.bezeichnung).join(', ')}
                {wesentlichGestoert.length === 0 && ' — der Betrieb läuft weiter.'}
              </div>
            )}
          </div>

          {zahlen !== null && (
            <>
              {/* NFA-16 */}
              <div className="kachel kachel--still">
                <div className="kachel__wort">Datenbank</div>
                <div className="kachel__zahl">{zahlen.datenbank.groesse}</div>
                <div className="kachel__zusatz">
                  {zahl(zahlen.datenbank.verbindungen)}{' '}
                  {zahlen.datenbank.verbindungen === 1 ? 'offene Verbindung' : 'offene Verbindungen'}
                </div>
              </div>

              <div className="kachel kachel--still">
                <div className="kachel__wort">Protokolleinträge</div>
                <div className="kachel__zahl">{zahl(zahlen.protokoll.eintraegeGeschaetzt)}</div>
                <div className="kachel__zusatz">geschätzt</div>
              </div>

              {/*
                LOG-12: Nicht „seit wann protokolliert wird", sondern „läuft
                das Aufräumen". Das Protokoll ist die einzige Tabelle, die
                unbegrenzt wächst; ein Fristenlauf, der seit Wochen aussetzt,
                ist die Auskunft, die man braucht.
              */}
              <div
                className={`kachel ${zahlen.protokoll.letzterLauf === null ? 'kachel--warn' : 'kachel--still'}`}
              >
                <div className="kachel__wort">Letzter Fristenlauf</div>
                <div className="kachel__zahl">
                  {zahlen.protokoll.letzterLauf === null
                    ? '—'
                    : vorWieLange(zahlen.protokoll.letzterLauf)}
                </div>
                <div className="kachel__zusatz">
                  {zahlen.protokoll.letzterLauf === null
                    ? 'noch keiner gelaufen'
                    : (zahlen.protokoll.letzterLaufErgebnis ?? genau(zahlen.protokoll.letzterLauf))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="karte">
        <div className="karte__titel">
          <h2>Umsysteme</h2>
        </div>
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Zustand der Umsysteme</caption>
            <thead>
              <tr>
                <th scope="col">System</th>
                <th scope="col">Zustand</th>
                <th scope="col">Zuletzt erreicht</th>
                <th scope="col">Geprüft</th>
              </tr>
            </thead>
            <tbody>
              {laedt && (
                <tr>
                  <td colSpan={4}>Wird geladen …</td>
                </tr>
              )}
              {!laedt && zeilen.length === 0 && (
                <tr>
                  <td colSpan={4}>Keine Umsysteme eingerichtet.</td>
                </tr>
              )}
              {zeilen.map((z) => (
                <tr key={z.kennung}>
                  <th scope="row">
                    {z.bezeichnung}
                    {z.wesentlich && (
                      <>
                        {' '}
                        {/*
                          „Wesentlich“ heißt: Ohne dieses System arbeitet die
                          Anwendung nicht. Die Unterscheidung gehört auf die
                          Seite, weil sie die Dringlichkeit trägt — ein stummes
                          Navision ist ärgerlich, eine tote Datenbank ist Feierabend.
                        */}
                        <span className="marke marke--inaktiv">wesentlich</span>
                      </>
                    )}
                  </th>
                  <td>
                    <span className={`marke marke--${ZUSTANDSMARKE[z.zustand]}`}>
                      {ZUSTANDSNAME[z.zustand]}
                    </span>
                    {z.meldung !== null && <div className="hilfe">{z.meldung}</div>}
                  </td>
                  <td className="zahl" title={genau(z.letzteVerbindung)}>
                    {vorWieLange(z.letzteVerbindung)}
                  </td>
                  <td className="zahl" title={genau(z.geprueftAm)}>
                    {vorWieLange(z.geprueftAm)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* LOG-09: die automatischen Läufe, im Takt der Seite. */}
      <Hintergrundlaeufe takt={takt} />

      {/* ERR-07, ERR-10 */}
      <Fehlerablage takt={takt} />

      {/* ==================================================================
          DIE ANLAGE SELBST (BET-22, NFA-16, BP-41)
          ================================================================== */}
      {zahlen !== null && (
        <div className="spalten spalten--zwei">
          <div className="karte">
            <div className="karte__titel">
              <h2>Anlage</h2>
              <small>läuft seit {vorWieLange(zahlen.laeuftSeit)}</small>
            </div>
            {/*
              `dt` und `dd` stehen UNMITTELBAR in der Liste und nicht paarweise
              in einem `div`: `.angaben` ist ein zweispaltiges Raster über die
              direkten Kinder, und ein Zwischenelement legte beide in dieselbe
              Zelle.
            */}
            <dl className="angaben">
              {/* BP-41: „zu jedem Zeitpunkt ist feststellbar, welche Version im Einsatz war." */}
              <dt>Ausgelieferter Stand</dt>
              <dd>{zahlen.stand}</dd>
              <dt>Gestartet</dt>
              <dd>{genau(zahlen.laeuftSeit)}</dd>
            </dl>

            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption className="nur-vorlesen">Fachlicher Bestand</caption>
                <thead>
                  <tr>
                    <th scope="col">Gegenstand</th>
                    <th scope="col" className="zahl">
                      Anzahl
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zahlen.bestand.map((b) => (
                    <tr key={b.was}>
                      <th scope="row">{b.was}</th>
                      <td className="zahl">{zahl(b.anzahl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* DAT-25 */}
            <p className="feld__hilfe">
              Gezählt, nicht geschätzt. Gelöschtes und Abgesagtes ist nicht mitgezählt —
              ein logisch gelöschter Kunde steht noch in der Tabelle, aber nicht mehr im
              Bestand.
            </p>
          </div>

          <div className="karte">
            <div className="karte__titel">
              <h2>Größte Tabellen</h2>
              <small>{zahlen.datenbank.groesse} insgesamt</small>
            </div>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption className="nur-vorlesen">Die größten Tabellen der Datenbank</caption>
                <thead>
                  <tr>
                    <th scope="col">Tabelle</th>
                    <th scope="col" className="zahl">
                      Belegt
                    </th>
                    <th scope="col" className="zahl">
                      Zeilen
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zahlen.datenbank.tabellen.map((t) => (
                    <tr key={t.name}>
                      <th scope="row">
                        <code>{t.name}</code>
                      </th>
                      <td className="zahl">{t.groesse}</td>
                      <td className="zahl">{zahl(t.zeilenGeschaetzt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="feld__hilfe">
              <strong>Belegt</strong> ist die Tabelle mit ihren Indizes — bei einer
              Protokolltabelle mit vier Indizes ein Vielfaches der reinen Daten. Die
              Zeilenzahl ist <strong>geschätzt</strong>: Sie kommt aus dem Katalog und
              kostet nichts, während ein genaues Zählen bei jedem Seitenaufruf die
              größte Tabelle einmal ganz läse.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
